import { useState, useEffect, createContext, useContext } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { childAcsForLokSabha, normalizeConstituencyKey } from '../data/lsToAc';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await api.get('/auth/me');
          setUser(response.data);
        } catch (error) {
          console.error('Failed to fetch user:', error);
          logout();
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { access_token, user: userData } = response.data;

      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);

      toast.success('Logged in successfully');
      return userData;
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      toast.error(message);
      throw error;
    }
  };

  const register = async (email, password, full_name, role = 'level-1') => {
    try {
      await api.post('/auth/register', { email, password, full_name, role });
      return login(email, password);
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed';
      toast.error(message);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    toast.info('Logged out');
  };

  // Derived scope helpers used by route guards / map gating.
  const role = user?.role;
  const isSuperAdmin =
    user?.is_super_admin === true || role === 'superadmin' || role === 'super_admin';
  const isScoped =
    user?.is_scoped === true || ['mla', 'mp', 'nara_lokesh'].includes(role);
  const assignedConstituency = user?.assigned_constituency || null;
  const assignedLokSabha = user?.assigned_lok_sabha || null;
  const extraConstituencies = user?.extra_constituencies || [];

  // An MP's scope is their whole parliamentary seat, not a single AC. The
  // server already expands assigned_lok_sabha into its child ACs
  // (scopeMiddleware.buildScope), so without the same expansion here the
  // client guard would bounce an MP off every segment of their own seat but
  // their own — blocking the parliamentary drill-down entirely.
  const lokSabhaConstituencies = assignedLokSabha ? childAcsForLokSabha(assignedLokSabha) : [];

  const allowedConstituencies = [
    assignedConstituency,
    ...extraConstituencies,
    ...lokSabhaConstituencies,
  ].filter(Boolean);

  const canAccessConstituency = (name) => {
    if (isSuperAdmin || !isScoped) return true;
    return allowedConstituencies.some((c) => normalizeConstituencyKey(c) === normalizeConstituencyKey(name));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        register,
        logout,
        loading,
        isSuperAdmin,
        isScoped,
        assignedConstituency,
        assignedLokSabha,
        lokSabhaConstituencies,
        allowedConstituencies,
        canAccessConstituency,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
